import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, BookOpen, BarChart3, TrendingUp, Clock, Award } from "lucide-react";
import apiClient from "../../services/apiClient";

export const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="spinner"></div></div>;
  }

  if (!analytics) {
    return <div className="text-center py-8 text-charcoal-600">No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl sm:text-2xl font-semibold text-charcoal-800 font-display">Analytics Dashboard</h2>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal-600 text-xs sm:text-sm">Total Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-charcoal-800">{analytics.total_users}</p>
            </div>
            <Users className="w-6 sm:w-8 h-6 sm:h-8 text-sage-500" />
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal-600 text-xs sm:text-sm">Active Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-charcoal-800">{analytics.active_users}</p>
            </div>
            <Users className="w-6 sm:w-8 h-6 sm:h-8 text-blue-500" />
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal-600 text-xs sm:text-sm">Total Modules</p>
              <p className="text-xl sm:text-2xl font-semibold text-charcoal-800">{analytics.total_modules}</p>
            </div>
            <BookOpen className="w-6 sm:w-8 h-6 sm:h-8 text-sage-500" />
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal-600 text-xs sm:text-sm">Completions</p>
              <p className="text-xl sm:text-2xl font-semibold text-charcoal-800">{analytics.total_completions}</p>
            </div>
            <BarChart3 className="w-6 sm:w-8 h-6 sm:h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Weekly Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6 bg-gradient-to-r from-sage-50 to-sage-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sage-700 text-xs sm:text-sm font-medium">Neue User diese Woche</p>
              <p className="text-2xl sm:text-3xl font-bold text-sage-800">{analytics.new_users_this_week || 0}</p>
            </div>
            <TrendingUp className="w-8 sm:w-10 h-8 sm:h-10 text-sage-600" />
          </div>
        </div>

        <div className="card p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-700 text-xs sm:text-sm font-medium">Aktive User diese Woche</p>
              <p className="text-2xl sm:text-3xl font-bold text-blue-800">{analytics.active_users_this_week || 0}</p>
            </div>
            <Users className="w-8 sm:w-10 h-8 sm:h-10 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Module Completion Rates */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-sage-600" />
          Modul-Abschlussraten
        </h3>
        <div className="space-y-4">
          {analytics.module_completion_rates && analytics.module_completion_rates.length > 0 ? (
            analytics.module_completion_rates.map((module, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-charcoal-800 font-medium text-sm">{module.title}</span>
                  <span className="text-sage-600 font-medium text-sm">{module.rate}% ({module.completions})</span>
                </div>
                <div className="w-full bg-cream-200 rounded-full h-3">
                  <div 
                    className="bg-sage-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(module.rate, 100)}%` }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-charcoal-600 text-center py-4">Keine Modul-Daten vorhanden</p>
          )}
        </div>
      </div>

      {/* Average Time per Module */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-600" />
          Durchschnittliche Zeit pro Modul
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {analytics.avg_time_per_module && analytics.avg_time_per_module.length > 0 ? (
            analytics.avg_time_per_module.map((module, index) => (
              <div key={index} className="bg-blue-50 p-4 rounded-lg">
                <p className="text-charcoal-700 font-medium text-sm truncate">{module.title}</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {module.avg_minutes > 0 ? `${module.avg_minutes} min` : '-'}
                </p>
              </div>
            ))
          ) : (
            <p className="text-charcoal-600 text-center py-4 col-span-full">Keine Zeit-Daten vorhanden</p>
          )}
        </div>
      </div>

      {/* Top Users */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4 flex items-center">
          <Award className="w-5 h-5 mr-2 text-amber-500" />
          Top 10 Aktivste User
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-sage-200">
                <th className="text-left py-2 text-charcoal-600 font-medium text-sm">#</th>
                <th className="text-left py-2 text-charcoal-600 font-medium text-sm">User</th>
                <th className="text-right py-2 text-charcoal-600 font-medium text-sm">Abschlüsse</th>
                <th className="text-right py-2 text-charcoal-600 font-medium text-sm">Gesamtzeit</th>
              </tr>
            </thead>
            <tbody>
              {analytics.user_engagement && analytics.user_engagement.length > 0 ? (
                analytics.user_engagement.map((user, index) => (
                  <tr key={index} className="border-b border-cream-200">
                    <td className="py-3 text-charcoal-500">
                      {index < 3 ? (
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                          index === 0 ? 'bg-amber-100 text-amber-700' :
                          index === 1 ? 'bg-gray-200 text-gray-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>{index + 1}</span>
                      ) : (
                        <span className="text-charcoal-400">{index + 1}</span>
                      )}
                    </td>
                    <td className="py-3 text-charcoal-800 font-medium">{user.email}</td>
                    <td className="py-3 text-right text-sage-600 font-medium">{user.completions}</td>
                    <td className="py-3 text-right text-blue-600">{user.total_minutes > 0 ? `${user.total_minutes} min` : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-4 text-center text-charcoal-600">Keine User-Daten vorhanden</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Logins */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Tägliche Anmeldungen (Letzte 30 Tage)</h3>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {analytics.daily_logins && analytics.daily_logins.length > 0 ? (
            analytics.daily_logins.map((day, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-charcoal-800 font-medium mb-1 sm:mb-0">{day.date}</span>
                <span className="text-blue-600 font-medium text-sm sm:text-base">{day.logins} Anmeldungen</span>
              </div>
            ))
          ) : (
            <p className="text-charcoal-600 text-center py-4">Keine Anmeldedaten vorhanden</p>
          )}
        </div>
      </div>

      {/* Popular Modules */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Popular Modules</h3>
        <div className="space-y-3">
          {analytics.popular_modules.length > 0 ? (
            analytics.popular_modules.map((module, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-cream-50 rounded-lg">
                <span className="text-charcoal-800 font-medium mb-1 sm:mb-0">{module.title}</span>
                <span className="text-sage-600 font-medium text-sm sm:text-base">{module.completions} completions</span>
              </div>
            ))
          ) : (
            <p className="text-charcoal-600 text-center py-4">No module completions yet</p>
          )}
        </div>
      </div>

      {/* Daily Activity */}
      <div className="card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Daily Activity (Last 7 Days)</h3>
        <div className="space-y-3">
          {analytics.daily_activity.length > 0 ? (
            analytics.daily_activity.map((day, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-cream-50 rounded-lg">
                <span className="text-charcoal-800 font-medium mb-1 sm:mb-0">{day.date}</span>
                <span className="text-sage-600 font-medium text-sm sm:text-base">{day.completions} completions</span>
              </div>
            ))
          ) : (
            <p className="text-charcoal-600 text-center py-4">No activity data available</p>
          )}
        </div>
      </div>
    </div>
  );
};
